import React, {useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import Button from 'src/components-new/primitives/Button'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {useAppSelector} from 'src/hooks'
import {enablePush, getPushState, PushState} from 'src/services/push/webPush'

const DISMISSED_AT_KEY = 'webPush.bannerDismissedAt'
const DISMISS_DAYS = 7

function isDismissed(): boolean {
  try {
    const dismissedAt = Number(window.localStorage.getItem(DISMISSED_AT_KEY))
    return Boolean(dismissedAt) && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000
  } catch (error) {
    return false
  }
}

function dismiss(): void {
  try {
    window.localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()))
  } catch (error) {
    // storage unavailable: the banner simply comes back at the next visit
  }
}

// Shown after login to a user who chose Push among the therapy reminder types or the preferred
// contacts (activity notifications) while this device is not among the ones receiving them. Browsers only
// allow the permission prompt from a click, so the banner is that click. "Non ora" and the close
// button hide it for DISMISS_DAYS days on this device.
export default function PushActivationBanner(): JSX.Element | null {
  const therapyReminderTypes = useAppSelector(state => state.userReducer.data.therapyReminderTypes)
  const preferredContacts = useAppSelector(state => state.userReducer.data.preferredContacts)
  const webDeviceTokens = useAppSelector(state => state.userReducer.data.webDeviceTokens)
  const toast = useToast()
  const [state, setState] = useState<PushState>()
  const [visible, setVisible] = useState(false)
  const [busy, setBusy] = useState(false)

  const wantsPush = (therapyReminderTypes ?? []).includes('PUSH') || (preferredContacts ?? []).includes('PUSH')

  useEffect(() => {
    if (!wantsPush || isDismissed()) {
      setVisible(false)
      return
    }
    let mounted = true
    getPushState(webDeviceTokens).then(next => {
      if (!mounted) return
      setState(next)
      setVisible(next === 'inactive' || next === 'ios-install-required')
    }).catch(() => {})
    return () => { mounted = false }
  }, [wantsPush, webDeviceTokens])

  const onDismiss = () => {
    dismiss()
    setVisible(false)
  }

  const onEnable = async () => {
    setBusy(true)
    try {
      const next = await enablePush()
      if (next === 'active') {
        toast.success('Riceverai le notifiche su questo dispositivo.')
      } else if (next === 'denied') {
        toast.warning('Le notifiche sono bloccate: puoi consentirle dalle impostazioni del browser.')
      }
      if (next !== 'inactive') setVisible(false)
    } catch (error) {
      toast.error('Non è stato possibile attivare le notifiche. Riprova più tardi.')
    } finally {
      setBusy(false)
    }
  }

  if (!visible) return null

  if (state === 'ios-install-required') {
    return (
      <InlineMessage severity="info" title="Notifiche" onClose={onDismiss} sx={{mb: 2}}>
        Per riceverle su iPhone o iPad, tocca Condividi e poi &quot;Aggiungi alla schermata Home&quot;,
        quindi apri il portale da lì.
      </InlineMessage>
    )
  }

  return (
    <InlineMessage severity="info" title="Notifiche" onClose={onDismiss} sx={{mb: 2}}>
      <Box component="span" sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap'}}>
        <Typography component="span" variant="body2">
          Vuoi ricevere le notifiche su questo dispositivo?
        </Typography>
        <Box component="span" sx={{display: 'flex', gap: 1}}>
          <Button variant="ghost" onClick={onDismiss} disabled={busy}>Non ora</Button>
          <Button variant="primary" onClick={onEnable} loading={busy}>Attiva</Button>
        </Box>
      </Box>
    </InlineMessage>
  )
}
